import { useId, useState, type ReactNode } from 'react';
import { Haptic } from '../kit/haptics';
import { ErrorAlert } from './ErrorAlert';
import { Modal } from './Modal';

const TONE = { primary: 'btn-primary', warning: 'btn-warning', error: 'btn-error' } as const;

export interface ConfirmDialogProps {
  title: ReactNode;
  /** Concrete description of the effect (what stops, what is kept, what is lost). */
  children?: ReactNode;
  confirmLabel: string;
  tone?: keyof typeof TONE;
  /** When set, the user must type this exact text before confirming (destructive actions). */
  typedConfirmation?: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  error?: unknown;
  /** Extra condition (e.g. a required checkbox inside `children`). */
  confirmDisabled?: boolean;
  size?: 'md' | 'lg' | 'xl';
}

export function ConfirmDialog({ title, children, confirmLabel, tone = 'primary', typedConfirmation, onConfirm, onCancel, busy, error, confirmDisabled, size }: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const inputId = useId();
  const matches = !typedConfirmation || typed === typedConfirmation;
  const disabled = !matches || Boolean(busy) || Boolean(confirmDisabled);

  return (
    <Modal title={title} onClose={onCancel} size={size}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!disabled) onConfirm();
        }}
      >
        <div className="space-y-3">
          {children}
          {typedConfirmation ? (
            <fieldset className="fieldset">
              <label htmlFor={inputId} className="fieldset-legend">
                <span>
                  Type <code className="rounded-md bg-base-200 px-1.5 py-0.5 font-mono text-[0.85em]">{typedConfirmation}</code> to confirm
                </span>
              </label>
              <input
                id={inputId}
                className={`input w-full font-mono ${typed && !matches ? 'input-error' : ''}`}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                aria-describedby={`${inputId}-hint`}
              />
              <p id={`${inputId}-hint`} className="label">
                {typed && !matches ? 'The text does not match yet.' : 'This cannot be undone.'}
              </p>
            </fieldset>
          ) : null}
          <ErrorAlert error={error} />
        </div>
        <div className="modal-action flex-wrap">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <Haptic disabled={disabled}>
            <button type="submit" className={`btn ${TONE[tone]}`} disabled={disabled}>
              {busy ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : null}
              {confirmLabel}
            </button>
          </Haptic>
        </div>
      </form>
    </Modal>
  );
}
